import React, { useState, useEffect } from 'react';
import {
  Button, Row, Col, UncontrolledTooltip
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { FileText, CheckSquare, Square, Send } from 'lucide-react';
import { fetchMatchedContractors, notifyContractors, updateContractorSelection } from './JobSubmissionService';
import ContractorCard from './ContractorCard';
import { Bid } from './BidShared';
import LoadingOverlay from '../../Components/LoadingOverlay';

interface ContractorMatchInfo {
  id: string;
  hasViewed: boolean;
  hasBid: boolean;
  isSelected: boolean;
  isInterested: boolean;
  status: string;
  dtInvited?: firebase.firestore.Timestamp | null;
  dtViewed?: firebase.firestore.Timestamp | null;
  lastViewedAt?: firebase.firestore.Timestamp | null;
}

interface MatchedContractor {
  id: string;
  contractorId: string;
  businessName: string;
  email: string;
  phone: string;
  website?: string;
  categories?: string[];
  generalInfo?: string;
  logo?: string;
  matchInfo?: ContractorMatchInfo;
}

interface ContractorWithBid extends MatchedContractor {
  bid?: Bid;
}

interface ConsolidatedContractorViewProps {
  jobId: string;
  bids: Bid[];
  onViewBid: (bidId: string) => void;
  onAcceptBid: (bid: Bid) => void;
  onDeleteBid?: (bid: Bid) => void;
  onContractorsInvited?: () => void;
  contractId?: string | null;
}

const ConsolidatedContractorView: React.FC<ConsolidatedContractorViewProps> = ({
  jobId,
  bids,
  onViewBid,
  onAcceptBid,
  onDeleteBid,
  onContractorsInvited,
  contractId
}) => {
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [contractors, setContractors] = useState<MatchedContractor[]>([]);
  const [contractorsWithBids, setContractorsWithBids] = useState<ContractorWithBid[]>([]);
  const [selectedContractors, setSelectedContractors] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  // Debug logging for state changes
  useEffect(() => {
    console.log('🔄 State changed - loading:', loading, 'inviting:', inviting, 'resetting:', resetting);
  }, [loading, inviting, resetting]);

  useEffect(() => {
    fetchContractors();
  }, [jobId, bids]);

  const fetchContractors = async () => {
    setLoading(true);
    try {
      // Get all matched contractors
      const matchedContractors = await fetchMatchedContractors(jobId) as MatchedContractor[];

      // Initialize selected contractors from those already marked as selected in the database
      const initialSelected = new Set<string>();
      matchedContractors.forEach(contractor => {
        if (contractor.matchInfo?.isSelected) {
          initialSelected.add(contractor.contractorId);
        }
      });

      setContractors(matchedContractors);
      setSelectedContractors(initialSelected);

      // Associate bids with contractors
      const contractorBidsMap = new Map<string, Bid[]>();
      bids.forEach(bid => {
        if (bid.contractorId) {
          if (!contractorBidsMap.has(bid.contractorId)) {
            contractorBidsMap.set(bid.contractorId, []);
          }
          contractorBidsMap.get(bid.contractorId)?.push(bid);
        }
      });

      // Combine contractors with their bids
      let contractorsWithBidsData: ContractorWithBid[] = [];

      matchedContractors.forEach(contractor => {
        if ((contractor.matchInfo && contractor.matchInfo.hasBid) ||
          (contractor.contractorId && contractorBidsMap.has(contractor.contractorId))) {

          // Get all bids for this contractor
          const contractorBids = contractorBidsMap.get(contractor.contractorId) || [];

          if (contractorBids.length > 0) {
            // Create a contractor entry for each bid
            contractorBids.forEach(bid => {
              contractorsWithBidsData.push({
                ...contractor,
                bid: bid
              });
            });
          } else {
            // If contractor is marked as having bid but no bid found
            contractorsWithBidsData.push({
              ...contractor,
              bid: undefined
            });
          }
        }
      });

      setContractorsWithBids(contractorsWithBidsData);
    } catch (error) {
      console.error('Error fetching contractors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    const isDev = process.env.REACT_APP_ENVIRONMENT === 'DEV';
    const allIds = new Set(contractors
      .filter(c => isDev ? true : (c.matchInfo?.status !== 'invited' && !c.matchInfo?.hasBid))
      .map(c => c.contractorId));
    setSelectedContractors(allIds);

    // Update all contractors' selection status in the database
    contractors.forEach(contractor => {
      if (isDev || (contractor.matchInfo?.status !== 'invited' && !contractor.matchInfo?.hasBid)) {
        updateContractorSelection(jobId, contractor.contractorId, true)
          .catch(error => console.error('Error updating contractor selection:', error));
      }
    });
  };

  const handleDeselectAll = () => {
    setSelectedContractors(new Set());

    // Update all contractors' selection status in the database
    contractors.forEach(contractor => {
      updateContractorSelection(jobId, contractor.contractorId, false)
        .catch(error => console.error('Error updating contractor selection:', error));
    });
  };

  const handleToggleContractor = (contractorId: string, isSelected: boolean) => {
    const newSelected = new Set(selectedContractors);

    if (!isSelected) {
      newSelected.delete(contractorId);
      updateContractorSelection(jobId, contractorId, false)
        .catch(error => console.error('Error updating contractor selection:', error));
    } else {
      newSelected.add(contractorId);
      updateContractorSelection(jobId, contractorId, true)
        .catch(error => console.error('Error updating contractor selection:', error));
    }

    setSelectedContractors(newSelected);
  };

  const handleResetToMatched = async () => {
    console.log('🔄 DEV: Resetting all contractors to matched status');
    setResetting(true);

    try {
      const firestore = firebase.firestore();
      const batch = firestore.batch();

      // Get all job-contractor matches for this job
      const matchesSnapshot = await firestore
        .collection('jobContractorMatches')
        .where('jobId', '==', jobId)
        .get();

      // Reset each match to 'matched' status
      matchesSnapshot.docs.forEach(doc => {
        batch.update(doc.ref, {
          status: 'matched',
          dtInvited: null,
          dtViewed: null,
          lastViewedAt: null,
          isSelected: false,
          isInterested: false
        });
      });

      // Also reset in contractor's matched-jobs collection
      for (const contractor of contractors) {
        if (contractor.contractorId) {
          const contractorJobRef = firestore
            .collection('contractors')
            .doc(contractor.contractorId)
            .collection('matched-jobs')
            .doc(jobId);
          
          batch.update(contractorJobRef, {
            status: 'matched',
            isSelected: false,
            isInterested: false
          });
        }
      }

      await batch.commit();
      console.log('✅ DEV: Reset completed, refreshing contractor list');
      
      // Clear selected contractors
      setSelectedContractors(new Set());
      
      // Refresh the contractor list
      await fetchContractors();
      
    } catch (error) {
      console.error('❌ DEV: Error resetting contractors:', error);
      alert('Failed to reset contractors. Check console for details.');
    } finally {
      setResetting(false);
    }
  };

  const handleInviteContractors = async () => {
    console.log('🚀 handleInviteContractors called');
    console.log('📊 selectedContractors.size:', selectedContractors.size);
    
    if (selectedContractors.size === 0) {
      alert('Please select at least one contractor to invite.');
      return;
    }

    console.log('⏳ Setting inviting to true');
    setInviting(true);
    
    // Add a small delay to ensure state update is processed
    setTimeout(() => {
      console.log('✅ Inviting state should now be true, overlay should be visible');
    }, 100);

    try {
      // Convert Set to Array for the API call
      const contractorIdsToInvite = Array.from(selectedContractors);
      console.log('📤 About to notify contractors:', contractorIdsToInvite);
      
      await notifyContractors(jobId, contractorIdsToInvite);
      console.log('✅ notifyContractors completed');

      // Refresh the contractor list to show updated status
      console.log('🔄 Refreshing contractor list');
      await fetchContractors();
      console.log('✅ fetchContractors completed');

      if (onContractorsInvited) {
        console.log('📢 Calling onContractorsInvited callback');
        onContractorsInvited();
      }
    } catch (error) {
      console.error('❌ Error inviting contractors:', error);
      alert('Failed to invite contractors. Please try again.');
    } finally {
      console.log('🏁 Setting inviting to false');
      setInviting(false);
    }
  };

  const handleViewProfile = (contractor: MatchedContractor) => {
    navigate(`/contractor-profile/${contractor.contractorId}`);
  };

  // Create a set of contractor IDs who have bids
  const contractorIdsWithBids = new Set(contractorsWithBids.map(c => c.contractorId));

  // Group contractors by their status
  const accepted = contractorsWithBids.filter(c => c.bid?.status === 'Accepted');
  const submitted = contractorsWithBids.filter(c =>
    c.bid && c.bid.status !== 'Accepted'
  );
  const invited = contractors.filter(c =>
    c.matchInfo?.status === 'invited' && !contractorIdsWithBids.has(c.contractorId)
  );

  // DEV environment override for matched contractors
  const isDev = process.env.REACT_APP_ENVIRONMENT === 'DEV';
  let matched: MatchedContractor[];

  if (isDev) {
    // Override: include both 'matched' and 'invited' status for testing, always allow checkboxes
    matched = contractors.filter(c =>
      (c.matchInfo?.status === 'matched' || c.matchInfo?.status === 'invited') && 
      !contractorIdsWithBids.has(c.contractorId)
    );
  } else {
    // Normal behavior: only 'matched' status
    matched = contractors.filter(c =>
      c.matchInfo?.status === 'matched' && !contractorIdsWithBids.has(c.contractorId)
    );
  }

  const showInviteButtons = matched.length > 0;

  return (
    <div style={{ position: 'relative', minHeight: '200px' }}>
      {/* Single LoadingOverlay that handles loading, inviting, and resetting states */}
      <LoadingOverlay 
        isVisible={loading || inviting || resetting} 
        text={loading ? "BidBro" : resetting ? "DEV Reset Tool" : "BidBro Job Notifier"} 
        subtext={loading 
          ? "Loading job details..."
          : resetting 
          ? "Resetting all contractors back to matched status for testing"
          : `Sending notifications to ${selectedContractors.size} pros!`
        }
        size="medium" 
      />

      {/* Debug info - remove in production */}
      {process.env.REACT_APP_ENVIRONMENT === 'DEV' && (
        <div style={{ 
          position: 'fixed', 
          top: '10px', 
          right: '10px', 
          background: 'rgba(0,0,0,0.8)', 
          color: 'white', 
          padding: '10px', 
          borderRadius: '5px',
          fontSize: '12px',
          zIndex: 10000
        }}>
          <div>Loading: {loading ? 'true' : 'false'}</div>
          <div>Inviting: {inviting ? 'true' : 'false'}</div>
          <div>Resetting: {resetting ? 'true' : 'false'}</div>
          <div>Selected: {selectedContractors.size}</div>
        </div>
      )}

      {!loading && (
        <>
          {/* DEV Reset Button */}
          {process.env.REACT_APP_ENVIRONMENT === 'DEV' && (
            <div className="d-flex justify-content-start mb-4">
              <Button
                color="warning"
                onClick={handleResetToMatched}
                disabled={resetting || inviting}
                className="me-2"
              >
                {resetting ? (
                  <>
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Resetting...
                  </>
                ) : (
                  <>
                    🔄 DEV: Reset to Matched Status
                  </>
                )}
              </Button>
              <small className="text-muted align-self-center">
                Resets all contractors back to "matched" status for testing
              </small>
            </div>
          )}

          {showInviteButtons && (
            <div className="d-flex justify-content-end align-items-center mb-4">
              <Button
                color="secondary"
                className="me-2 d-none d-md-block"
                onClick={handleSelectAll}
                disabled={inviting || resetting || matched.length === 0}
              >
                Select All
              </Button>
              <Button
                color="secondary"
                className="me-2 d-md-none"
                onClick={handleSelectAll}
                disabled={inviting || resetting || matched.length === 0}
                id="selectAllBtn"
              >
                <CheckSquare size={18} />
              </Button>
              <UncontrolledTooltip
                placement="top"
                target="selectAllBtn"
                transition={{ timeout: 150 }} >
                Select All
              </UncontrolledTooltip>

              <Button
                color="secondary"
                className="me-2 d-none d-md-block"
                onClick={handleDeselectAll}
                disabled={inviting || resetting || selectedContractors.size === 0}
              >
                Deselect All
              </Button>
              <Button
                color="secondary"
                className="me-2 d-md-none"
                onClick={handleDeselectAll}
                disabled={inviting || resetting || selectedContractors.size === 0}
                id="deselectAllBtn"
              >
                <Square size={18} />
              </Button>
              <UncontrolledTooltip
                placement="top"
                target="deselectAllBtn"
                transition={{ timeout: 150 }}>
                Deselect All
              </UncontrolledTooltip>

              <Button
                color="primary"
                onClick={handleInviteContractors}
                disabled={inviting || resetting || selectedContractors.size === 0}
                className="d-none d-md-block"
              >
                <FileText className="me-2" />
                Invite Selected Contractors
              </Button>
              <Button
                color="primary"
                onClick={handleInviteContractors}
                disabled={inviting || resetting || selectedContractors.size === 0}
                className="d-md-none"
                id="inviteBtn"
              >
                <Send size={18} />
              </Button>
              <UncontrolledTooltip
                placement="top"
                target="inviteBtn"
                transition={{ timeout: 150 }} >
                Invite Selected Contractors
              </UncontrolledTooltip>
            </div>
          )}

          {/* Accepted bids */}
          {accepted.length > 0 && (
            <div className="mb-4">
              <h5 className="mb-3">Accepted Bids ({accepted.length})</h5>
              <Row className="g-3">
                {accepted.map((contractor) => (
                  <Col key={`accepted-${contractor.id}-${contractor.bid?.id}`} xs={12} md={6} lg={4}>
                    <ContractorCard
                      contractor={contractor}
                      bid={contractor.bid}
                      onViewBid={onViewBid}
                      contractId={contractId}
                    />
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {/* Submitted bids */}
          {submitted.length > 0 && (
            <div className="mb-4">
              <h5 className="mb-3">Submitted Bids ({submitted.length})</h5>
              <Row className="g-3">
                {submitted.map((contractor) => (
                  <Col key={`submitted-${contractor.id}-${contractor.bid?.id}`} xs={12} md={6} lg={4}>
                    <ContractorCard
                      contractor={contractor}
                      bid={contractor.bid}
                      onViewBid={onViewBid}
                      onAcceptBid={onAcceptBid}
                      onDeleteBid={onDeleteBid}
                    />
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {/* Invited contractors */}
          {invited.length > 0 && (
            <div className="mb-4">
              <h5 className="mb-3">Invited Contractors ({invited.length})</h5>
              <Row className="g-3">
                {invited.map((contractor) => (
                  <Col key={`invited-${contractor.id}`} xs={12} md={6} lg={4}>
                    <ContractorCard
                      contractor={contractor}
                      withCheckbox={false}
                      disabled={inviting || resetting}
                    />
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {/* Matched but not invited contractors */}
          {matched.length > 0 && (
            <div className="mb-4">
              <h5 className="mb-3">
                Potential Contractor Matches ({matched.length})
                {isDev && (
                  <small className="text-muted ms-2">(DEV MODE)</small>
                )}
              </h5>
              <Row className="g-3">
                {matched.map((contractor) => (
                  <Col key={`matched-${contractor.id}`} xs={12} md={6} lg={4}>
                    <ContractorCard
                      contractor={contractor}
                      withCheckbox={true}
                      isSelected={selectedContractors.has(contractor.contractorId)}
                      onSelect={handleToggleContractor}
                      disabled={isDev ? false : (inviting || resetting)}
                    />
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {contractors.length === 0 && contractorsWithBids.length === 0 && !loading && (
            <div className="text-center p-4 bg-light rounded">
              <p className="mb-0">No matching contractors or bids found for this job.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ConsolidatedContractorView;